import { forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { useFieldContext } from '@bridebook/aura';
import { cn } from '../../lib/utils/cn';
import { inputBaseRing, inputErrorVariant, inputDisabledVariant } from '../input/input-variants';

const TEXTAREA_LINE_HEIGHT = 24;
const TEXTAREA_VERTICAL_PADDING = 10;

const textareaVariants = cva(
  cn(
    'w-full rounded-lg px-4 py-[var(--textarea-vertical-padding)] leading-[var(--textarea-line-height)]',
    'typo-base text-space placeholder:text-space-60',
    'transition-[background-color,color,box-shadow,border] duration-150 outline-none',
    'disabled:cursor-not-allowed',
    inputBaseRing,
    'focus:bg-white focus:ring-2',
  ),
  {
    variants: {
      variant: {
        default: 'bg-space-04 group-hover:data-[error=false]:not-focus:not-disabled:bg-space-08',
        white:
          'border border-space-40 bg-white focus:border-transparent group-hover:data-[error=false]:not-focus:not-disabled:bg-space-04',
      },
      error: inputErrorVariant,
      disabled: inputDisabledVariant,
      resizeHandle: {
        true: 'resize-y',
        false: 'resize-none',
      },
    },
    defaultVariants: {
      variant: 'default',
      error: false,
      disabled: false,
      resizeHandle: true,
    },
  },
);

export interface TextareaProps
  extends Omit<React.ComponentPropsWithoutRef<'textarea'>, 'disabled'>,
    VariantProps<typeof textareaVariants> {
  resizeHandle?: boolean;
  autoResize?: boolean;
  classNames?: {
    wrapper?: string;
  };
}

export const Textarea = forwardRef<HTMLTextAreaElement, TextareaProps>(
  (
    {
      className,
      classNames,
      error: errorProp,
      disabled: disabledProp,
      resizeHandle = true,
      autoResize = true,
      onChange,
      onMouseUp,
      value,
      rows,
      variant,
      ...props
    },
    ref,
  ) => {
    const context = useFieldContext();
    const disabled = disabledProp ?? context?.disabled ?? false;
    const error = errorProp ?? context?.error ?? false;
    const textareaRef = useRef<HTMLTextAreaElement | null>(null);

    const autoResizeDisabledByUserRef = useRef(false);
    const lastAutoHeightRef = useRef<number | null>(null);

    const setRefs = useCallback(
      (node: HTMLTextAreaElement | null) => {
        textareaRef.current = node;
        if (typeof ref === 'function') {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      },
      [ref],
    );

    const adjustInputHeight = useCallback(
      (element: HTMLTextAreaElement) => {
        if (!autoResize || autoResizeDisabledByUserRef.current) return;
        element.style.height = 'auto';
        const nextHeight = element.scrollHeight;
        element.style.height = `${nextHeight}px`;
        lastAutoHeightRef.current = nextHeight;
      },
      [autoResize],
    );

    // Reset manual-resize lock whenever the autoResize prop is toggled.
    useEffect(() => {
      autoResizeDisabledByUserRef.current = false;
    }, [autoResize]);

    // Handles programmatic value changes (controlled mode) and initial pre-filled values.
    // handleChange below covers uncontrolled mode (defaultValue / no value prop),
    // where `value` is always undefined so this effect only runs once on mount.
    useEffect(() => {
      if (textareaRef.current) {
        adjustInputHeight(textareaRef.current);
      }
    }, [value, adjustInputHeight]);

    // Handles user input. Needed for uncontrolled mode where `value` never changes
    // so the effect above doesn't fire on keystrokes.
    const handleChange: React.ChangeEventHandler<HTMLTextAreaElement> = (event) => {
      adjustInputHeight(event.target);
      onChange?.(event);
    };

    // Used to detect if the user has resized the textarea by dragging the resize handle.
    // It's mimicking how it works with CSS only solution field-sizing: "content" which doesn't work on mobile Safari.
    const handleMouseUp: React.MouseEventHandler<HTMLTextAreaElement> = (event) => {
      onMouseUp?.(event);

      if (!autoResize || !resizeHandle || lastAutoHeightRef.current === null) return;

      const currentHeight = Number.parseFloat(event.currentTarget.style.height);
      if (Number.isNaN(currentHeight)) return;

      if (Math.abs(currentHeight - lastAutoHeightRef.current) > 1) {
        autoResizeDisabledByUserRef.current = true;
      }
    };

    const minHeight = useMemo(() => {
      const singleLineHeight = TEXTAREA_LINE_HEIGHT + 2 * TEXTAREA_VERTICAL_PADDING;
      if (!rows || rows === 0) return `${singleLineHeight + TEXTAREA_LINE_HEIGHT}px`;
      return `${singleLineHeight + (rows - 1) * TEXTAREA_LINE_HEIGHT}px`;
    }, [rows]);

    return (
      <div className={cn('group flex', classNames?.wrapper)}>
        <textarea
          ref={setRefs}
          data-error={error ? 'true' : 'false'}
          disabled={disabled ?? false}
          style={
            {
              minHeight,
              '--textarea-line-height': `${TEXTAREA_LINE_HEIGHT}px`,
              '--textarea-vertical-padding': `${TEXTAREA_VERTICAL_PADDING}px`,
            } as React.CSSProperties
          }
          className={cn(
            textareaVariants({
              error,
              disabled: disabled ?? false,
              resizeHandle,
              variant,
            }),
            className,
          )}
          onChange={handleChange}
          onMouseUp={handleMouseUp}
          rows={rows}
          value={value}
          {...props}
        />
      </div>
    );
  },
);

Textarea.displayName = 'Textarea';
